import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

/**
 * Renders an SVG overlay that draws a polyline connecting the centers of each
 * agent node in execution order. The line fills up as agents complete and a
 * red pulse marker sits on the currently-active agent.
 *
 * Props:
 *  - containerRef:  ref to the wrapping div whose coords we use as origin
 *  - agentOrder:    string[] — execution order of agent ids
 *  - statusById:    { [id]: 'idle'|'thinking'|'working'|'completed'|'error' }
 */
export default function FlowOverlay({ containerRef, agentOrder, statusById }) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [pts, setPts] = useState([]); // [{x,y,id,status}]
  const rafRef = useRef(0);

  function recompute() {
    const root = containerRef.current;
    if (!root) return;
    const box = root.getBoundingClientRect();
    setSize({ w: box.width, h: box.height });
    const next = [];
    for (const id of agentOrder) {
      const el = root.querySelector(`[data-testid="agent-node-${id}"]`);
      if (!el) continue;
      const r = el.getBoundingClientRect();
      next.push({
        id,
        x: r.left - box.left + r.width / 2,
        y: r.top - box.top + r.height / 2,
        status: statusById[id] || "idle",
      });
    }
    setPts(next);
  }

  useLayoutEffect(() => {
    recompute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentOrder, statusById, containerRef]);

  useEffect(() => {
    function onResize() {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(recompute);
    }
    window.addEventListener("resize", onResize);
    const ro = new ResizeObserver(onResize);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => {
      window.removeEventListener("resize", onResize);
      ro.disconnect();
      cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (size.w === 0 || pts.length < 2) return null;

  // Build path "M x1 y1 L x2 y2 …"
  const fullPath = pts.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(" ");

  // Active marker = currently working/thinking agent (last in chain that's active)
  const activeIdx = pts.findIndex((p) => p.status === "thinking" || p.status === "working");
  const active = activeIdx >= 0 ? pts[activeIdx] : null;
  const completedCount = pts.filter((p) => p.status === "completed").length;
  const progress = completedCount / pts.length;

  return (
    <svg
      className="pointer-events-none absolute inset-0 z-10"
      width={size.w}
      height={size.h}
      data-testid="flow-overlay"
    >
      {/* gray dotted backbone */}
      <path
        d={fullPath}
        fill="none"
        stroke="rgba(0,180,230,0.22)"
        strokeWidth="1"
        strokeDasharray="2 6"
      />
      {/* progress trace */}
      <motion.path
        d={fullPath}
        fill="none"
        stroke="#00B4E6"
        strokeWidth="1.5"
        strokeLinecap="round"
        initial={false}
        animate={{ pathLength: Math.max(0.001, progress) }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      />
      {/* completed nodes — solid black squares */}
      {pts.map((p) =>
        p.status === "completed" ? (
          <rect key={p.id} x={p.x - 3} y={p.y - 3} width="6" height="6" fill="#00B4E6" />
        ) : null
      )}
      {/* error nodes */}
      {pts.map((p) =>
        p.status === "error" ? (
          <g key={p.id}>
            <line x1={p.x - 4} y1={p.y - 4} x2={p.x + 4} y2={p.y + 4} stroke="#FF5B5B" strokeWidth="1.5" />
            <line x1={p.x - 4} y1={p.y + 4} x2={p.x + 4} y2={p.y - 4} stroke="#FF5B5B" strokeWidth="1.5" />
          </g>
        ) : null
      )}
      {/* active pulse */}
      <AnimatePresence>
        {active && (
          <motion.g key={active.id}>
            <motion.circle
              cx={active.x}
              cy={active.y}
              r={4}
              fill="#00B4E6"
              initial={{ opacity: 0.9 }}
              animate={{ opacity: 0.9 }}
            />
            <motion.circle
              cx={active.x}
              cy={active.y}
              r={4}
              fill="none"
              stroke="#00B4E6"
              strokeWidth="1"
              initial={{ scale: 1, opacity: 0.7 }}
              animate={{ scale: 4, opacity: 0 }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
              style={{ transformOrigin: `${active.x}px ${active.y}px` }}
            />
          </motion.g>
        )}
      </AnimatePresence>
    </svg>
  );
}
